import React, { useState, useEffect } from 'react';
import { toast } from 'react-toast';
import '../../styles/modal.css';
import '../../styles/version-history.css';

const MAX_NAME_CHARS = 16;

const truncateByChars = (text = '', maxChars = MAX_NAME_CHARS) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { display: '', full: '', isTruncated: false };

  if (trimmed.length <= maxChars) {
    return { display: trimmed, full: trimmed, isTruncated: false };
  }

  return {
    display: `${trimmed.slice(0, maxChars)}...`,
    full: trimmed,
    isTruncated: true,
  };
};

const TestCaseAutoUpdateModalCopy = ({ isOpen, onClose, testCase, onApplyUpdates }) => {
  const [loading, setLoading] = useState(true);
  const [detectedChanges, setDetectedChanges] = useState([]);
  const [selectedChanges, setSelectedChanges] = useState({});
  const [updateReason, setUpdateReason] = useState('');
  const [versionType, setVersionType] = useState('minor');

  useEffect(() => {
    if (isOpen && testCase) {
      setLoading(true);
      
      // Reset state when modal opens
      setSelectedChanges({});
      setUpdateReason('');
      setVersionType('minor');
      
      // Simulate API call to detect changes
      setTimeout(() => {
        // Mock detected changes based on requirements or UI updates
        const mockDetectedChanges = [
          {
            id: 1,
            type: 'ui_change',
            element: 'Login Button',
            description: 'Login button has been moved from top-right to center of form',
            oldValue: 'Click login button in the top-right corner',
            newValue: 'Click login button at the bottom of the form',
            confidence: 95,
            affectedStep: 3
          },
          {
            id: 2,
            type: 'field_addition',
            element: 'Remember Me Checkbox',
            description: 'New "Remember Me" checkbox added to login form',
            oldValue: null,
            newValue: 'Check "Remember Me" checkbox if you want to stay logged in',
            confidence: 90,
            affectedStep: 'new'
          },
          {
            id: 3,
            type: 'requirement_change',
            element: 'Two-Factor Authentication',
            description: 'Two-factor authentication is now required for all logins',
            oldValue: null,
            newValue: 'Complete the two-factor authentication by entering the code sent to your email',
            confidence: 98,
            affectedStep: 'new'
          },
          {
            id: 4,
            type: 'expected_result_change',
            element: 'Login Success',
            description: 'Expected result has changed due to new dashboard layout',
            oldValue: 'User is logged in successfully',
            newValue: 'User is logged in successfully and redirected to the new dashboard',
            confidence: 85,
            affectedStep: 'expected'
          }
        ];
        
        setDetectedChanges(mockDetectedChanges);
        
        // Initialize selected changes (all selected by default)
        const initialSelectedChanges = {};
        mockDetectedChanges.forEach(change => {
          initialSelectedChanges[change.id] = true;
        });
        setSelectedChanges(initialSelectedChanges);
        
        setLoading(false);
      }, 1500);
    }
  }, [isOpen, testCase]);

  const handleChangeSelection = (changeId) => {
    setSelectedChanges(prev => ({
      ...prev,
      [changeId]: !prev[changeId]
    }));
  };

  const handleApplyUpdates = () => {
    if (!updateReason.trim()) {
      toast.error('Please provide a reason for the update');
      return;
    }
    
    // Filter only selected changes
    const changesToApply = detectedChanges.filter(change => selectedChanges[change.id]);
    
    if (changesToApply.length === 0) {
      toast.error('Please select at least one change to apply');
      return;
    }
    
    // Call the parent handler with selected changes
    if (onApplyUpdates) {
      onApplyUpdates({
        changes: changesToApply,
        reason: updateReason,
        versionType: versionType
      });
    }
    
    // Close the modal
    onClose();
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'ui_change':
        return '🖥️';
      case 'field_addition':
        return '➕';
      case 'requirement_change':
        return '📝';
      case 'expected_result_change':
        return '✅';
      default:
        return '🔄';
    }
  };

  const getChangeTypeLabel = (type) => {
    switch (type) {
      case 'ui_change':
        return 'UI Change';
      case 'field_addition':
        return 'Field Added';
      case 'requirement_change':
        return 'Requirement Change';
      case 'expected_result_change':
        return 'Expected Result Change';
      default:
        return 'Change';
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'ui_change':
        return '#1890ff';
      case 'field_addition':
        return '#52c41a';
      case 'requirement_change':
        return '#fa8c16';
      case 'expected_result_change':
        return '#722ed1';
      default:
        return '#666';
    }
  };

  if (!isOpen) return null;
  const truncatedTestCaseName = truncateByChars(testCase?.name);

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose} style={{zIndex:14000}} ></div>
      <div className="modal auto-update-modal" style={{ maxWidth: "800px", width: "90%", maxHeight: "90vh", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)" ,zIndex:14500}}>
        <div className="modal-header" style={{ borderBottom: "1px solid #e9ecef", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: "0", fontSize: "1.4rem", fontWeight: "600", color: "#212529" }}>
            Auto-Update Test Case
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: "24px", maxHeight: "60vh", overflowY: "auto" }}>
          {loading ? (
            <div className="loading-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
              <div className="loading-spinner" style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              <p style={{ marginTop: "16px", color: "#666" }}>Detecting changes in requirements and UI...</p>
            </div>
          ) : (
            <>
              <div className="test-case-info" style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1rem" }}>
                  Test Case:{' '}
                  <span title={truncatedTestCaseName.isTruncated ? truncatedTestCaseName.full : undefined}>
                    {truncatedTestCaseName.display}
                  </span>
                </h3>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#666" }}>
                  Our system has detected potential changes in requirements or UI that may affect this test case.
                  Please review the suggested updates below.
                </p>
              </div>
              
              <div className="detected-changes" style={{ marginBottom: "24px" }}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: "1.1rem" }}>Detected Changes</h3>
                
                {detectedChanges.length === 0 ? (
                  <p>No changes detected.</p>
                ) : (
                  <div className="changes-list">
                    {detectedChanges.map(change => (
                      <div 
                        key={change.id} 
                        className="change-item"
                        style={{
                          padding: "16px",
                          marginBottom: "16px",
                          borderRadius: "8px",
                          border: "1px solid #eee",
                          backgroundColor: selectedChanges[change.id] ? "#f0f9ff" : "white",
                          position: "relative"
                        }}
                      >
                        <div className="change-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "1.2rem" }}>{getChangeTypeIcon(change.type)}</span>
                            <div>
                              <h4 style={{ margin: "0 0 4px 0", fontSize: "1rem", fontWeight: "600" }}>{change.element}</h4>
                              <span 
                                style={{ 
                                  fontSize: "0.8rem", 
                                  padding: "2px 8px", 
                                  borderRadius: "10px", 
                                  backgroundColor: `${getChangeTypeColor(change.type)}20`, 
                                  color: getChangeTypeColor(change.type),
                                  fontWeight: "500"
                                }}
                              >
                                {getChangeTypeLabel(change.type)}
                              </span>
                              <span 
                                style={{ 
                                  fontSize: "0.8rem", 
                                  marginLeft: "8px",
                                  padding: "2px 8px", 
                                  borderRadius: "10px", 
                                  backgroundColor: "#f0f0f0", 
                                  color: "#666"
                                }}
                              >
                                Confidence: {change.confidence}%
                              </span>
                            </div>
                          </div>
                          <label className="change-select" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                            <input 
                              type="checkbox" 
                              checked={selectedChanges[change.id] || false}
                              onChange={() => handleChangeSelection(change.id)}
                              style={{ marginRight: "8px", width: "16px", height: "16px" }}
                            />
                            <span style={{ fontSize: "0.9rem" }}>Apply this change</span>
                          </label>
                        </div>
                        
                        <p style={{ margin: "0 0 16px 0", fontSize: "0.9rem" }}>{change.description}</p>
                        
                        <div className="change-details" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {change.oldValue && (
                            <div className="old-value" style={{ backgroundColor: "#fff1f0", padding: "12px", borderRadius: "6px", fontSize: "0.9rem" }}>
                              <div style={{ fontWeight: "500", marginBottom: "4px" }}>Current Value:</div>
                              <div>{change.oldValue}</div>
                            </div>
                          )}
                          
                          <div className="new-value" style={{ backgroundColor: "#f6ffed", padding: "12px", borderRadius: "6px", fontSize: "0.9rem" }}>
                            <div style={{ fontWeight: "500", marginBottom: "4px" }}>Suggested Value:</div>
                            <div>{change.newValue}</div>
                          </div>
                          
                          <div className="affected-step" style={{ fontSize: "0.85rem", color: "#666", marginTop: "4px" }}>
                            {change.affectedStep === 'new' ? (
                              <span>This will add a new step to the test case</span>
                            ) : change.affectedStep === 'expected' ? (
                              <span>This will update the expected result</span>
                            ) : (
                              <span>This will update step #{change.affectedStep}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="update-options" style={{ marginBottom: "24px" }}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: "1.1rem" }}>Update Options</h3>
                
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>Version Update Type:</label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input 
                        type="radio" 
                        name="versionType" 
                        value="patch" 
                        checked={versionType === 'patch'} 
                        onChange={() => setVersionType('patch')}
                        style={{ marginRight: "8px" }}
                      />
                      <span>Patch (1.0.1)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input 
                        type="radio" 
                        name="versionType" 
                        value="minor" 
                        checked={versionType === 'minor'} 
                        onChange={() => setVersionType('minor')}
                        style={{ marginRight: "8px" }}
                      />
                      <span>Minor (1.1.0)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input 
                        type="radio" 
                        name="versionType" 
                        value="major" 
                        checked={versionType === 'major'} 
                        onChange={() => setVersionType('major')}
                        style={{ marginRight: "8px" }}
                      />
                      <span>Major (2.0.0)</span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>Update Reason:</label>
                  <textarea 
                    value={updateReason}
                    onChange={(e) => setUpdateReason(e.target.value)}
                    placeholder="Provide a reason for these updates (required)"
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      minHeight: "80px",
                      fontSize: "0.9rem"
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="modal-footer" style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa",
          gap: "12px"
        }}>
          <button 
            className="btn btn-outline" 
            style={{ 
              padding: "8px 16px", 
              backgroundColor: "white", 
              border: "1px solid #ccc", 
              borderRadius: "4px", 
              cursor: "pointer" 
            }} 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            style={{ 
              padding: "8px 16px", 
              backgroundColor: "#007bff", 
              color: "white",
              border: "none", 
              borderRadius: "4px", 
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }} 
            onClick={handleApplyUpdates}
            disabled={loading}
          >
            <span style={{ fontSize: "16px" }}>🔄</span>
            Apply Selected Updates
          </button>
        </div>
      </div>
    </>
  );
};

export default TestCaseAutoUpdateModalCopy;